import { ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { FadeUp, PageHero, Eyebrow } from "@/components/Reveal";

const ROLES = [
  { title: "Chat Manager", desc: "Gestione conversazioni e vendite con script professionali. Eccellente padronanza dell'italiano scritto e doti di persuasione testuale.", testid: "role-chat-manager" },
  { title: "Growth Strategist", desc: "Piani di crescita multi-piattaforma basati su dati e sperimentazione continua.", testid: "role-growth-strategist" },
  { title: "Media Buyer", desc: "Gestione budget pubblicitari e acquisizione traffico con focus sul ritorno misurabile.", testid: "role-media-buyer" },
  { title: "Video Editor", desc: "Editing veloce e riconoscibile per contenuti short-form ad alte prestazioni.", testid: "role-video-editor" },
  { title: "Talent Scout", desc: "Individuazione e primo contatto con profili ad alto potenziale nella creator economy.", testid: "role-talent-scout" },
];

const CULTURE = [
  { title: "Performance", text: "Risultati misurabili, obiettivi chiari, crescita meritocratica." },
  { title: "Riservatezza", text: "Lo stesso standard di privacy che garantiamo ai creator vale per il team." },
  { title: "Remote-first", text: "Lavoriamo da ovunque, con processi e responsabilità definite." },
  { title: "Formazione continua", text: "Sales, marketing e creator economy: cresci dentro un sistema che evolve." },
];

export default function LavoraConNoi() {
  const { openContact } = useSite();

  return (
    <main data-testid="lavora-page">
      <PageHero
        testid="lavora-hero"
        eyebrow="Lavora con noi"
        lines={[
          "Standard d'eccellenza.",
          <span key="l2" className="gold-text gold-text-animated italic">Opportunità reali.</span>,
        ]}
        sub="IEM — Iconic Elite Management S.R.L.S. è una società italiana di digital management in costante espansione. Offriamo un ambiente meritocratico, contrattualizzato e altamente strutturato per professionisti ambiziosi, precisi e orientati alle performance."
      />

      <section className="py-14 lg:py-20" data-testid="roles-section">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Eyebrow>Posizioni aperte</Eyebrow>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-[#1A1A1A] bg-[#1A1A1A] sm:grid-cols-2 lg:grid-cols-3">
            {ROLES.map((r, i) => (
              <FadeUp key={r.title} delay={i * 0.06} className="group flex flex-col bg-[#0b0b0b] p-8 transition-colors duration-500 hover:bg-[#111111]">
                <div data-testid={r.testid} className="flex h-full flex-col">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-xl sm:text-2xl text-[#F5F3EE]">{r.title}</h3>
                    <span className="border border-gold/30 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-gold">
                      Aperta
                    </span>
                  </div>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-[#969696]">{r.desc}</p>
                  <button
                    data-testid={`${r.testid}-apply`}
                    onClick={() => openContact("team")}
                    className="mt-6 inline-flex items-center gap-2 text-[12px] font-semibold tracking-[0.18em] text-gold transition-colors hover:text-gold-bright"
                  >
                    CANDIDATI <ArrowRight size={13} />
                  </button>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="culture-section">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Eyebrow>La cultura IEM</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5F3EE]">
              Processi chiari. Standard d'eccellenza. <span className="italic text-gold">Risultati concreti.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-[#969696]">
              Non crediamo nell'improvvisazione: offriamo un contesto professionale, trasparente e orientato alle
              performance, dove competenza e dedizione vengono costantemente valorizzate.
            </p>
          </FadeUp>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {CULTURE.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.08} className="border-t border-gold/30 pt-6">
                <div data-testid={`culture-${c.title.toLowerCase().replace(/\s/g, "-")}`}>
                  <h3 className="font-serif text-xl text-[#F5F3EE]">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#969696]">{c.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.25} className="mt-14">
            <button
              data-testid="lavora-page-cta"
              onClick={() => openContact("team")}
              className="inline-flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-bold tracking-[0.18em] text-[#080808] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(229,196,90,0.25)]"
            >
              CANDIDATI ORA <ArrowRight size={15} />
            </button>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
